/**
 * The screener bar — one bar for what you screen (`docs/plans/screener-bar.md`,
 * mock `docs/screener-bar-mock.html`).
 *
 * Read left to right: **screens › universe › this screen's settings or the
 * chips › tools.** It replaced three rows — the group-type buttons, the
 * picked-group chips, and the Recs bar — none of which said which screen was
 * open.
 *
 * The rules that are arithmetic live in `lib/screener-bar.ts`; this file keeps
 * only what needs a browser: measuring itself (tall or wide, the chips' fill)
 * and the popovers.
 *
 * ## The chips are laid out and then measured
 *
 * Kept from the bar this replaced, because each rule is a silent failure:
 *
 * - **Measure an off-screen ruler that always holds every chip**, never the
 *   visible row — measuring the row latches at zero (it holds only what was
 *   shown last, so from none there is nothing to measure).
 * - **A ruler chip and a real chip render the same markup** (`chipInner`), or
 *   every width describes a chip that is not on screen.
 * - **The `+N ▾` button is paid for once**: it is a sibling of the chip box,
 *   so its width is added back to the budget before `fillRow` reserves for it.
 * - **A zero width is "not laid out yet"**: show everything, and the next
 *   measurement corrects it.
 *
 * ## Theming
 *
 * `var(--qf-*)` only, and "on" is an accent fill with background-coloured text,
 * never accent-coloured text — the accent is light on dark themes and dark on
 * White, so coloured text is unreadable on one of them.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";

import type { SymbolGroup, SymbolGroupType } from "../api/symbols";
import { CHIP_GAP, fillRow, type OneRow } from "../lib/chip-rows";
import type { CombineMode, GroupSelection, PickedGroup } from "../lib/group-selection";
import type { ScreenerPlacement } from "../lib/layout-templates";
import { marketByCode } from "../lib/market";
import {
  ALL_KINDS,
  COLUMNS_WORD_FROM,
  barPicked,
  chipLabel,
  isTall,
  kindRows,
  onCount,
  pickSingle,
  removeSingle,
  recentScreens,
  THREE_SCREENS_FROM,
  screenSections,
  universeLabel,
  withView,
} from "../lib/screener-bar";
import { isClub, type Screen } from "../lib/screens";
import { mergeAcrossMarkets, type MergedGroup } from "../lib/touch-screener";
import { CloseIcon, Menu, MenuRow } from "./Menu";

/** The one group type whose groups the caller makes — see `onNewList`. */
const TRACK_LIST_TYPE = "trackList";

export interface ScreenerBarProps {
  // ── screens ──
  screens: readonly Screen[];
  /** The panel's `screenRef`; absent falls back to the strip's default. */
  current?: string;
  /** What was current before it — see `recallPrevious`. */
  previous?: string;
  /** The panel's recent screens, newest first; shown after the current one as room allows. */
  recent?: readonly string[];
  onPickScreen: (key: string) => void;
  /** ＋ New in the ▾ list. Omitted ⇒ not offered. */
  onNewScreen?: () => void;
  /** ✎ Edit this screen — the docked editor. Omitted ⇒ not offered. */
  onEditScreen?: (via?: "columns") => void;

  // ── universe ──
  types: readonly SymbolGroupType[];
  groupsByType: ReadonlyMap<string, readonly SymbolGroup[]>;
  selection: GroupSelection;
  onSelectionChange: (next: GroupSelection) => void;
  /** Codes with no screen data yet: shown greyed with a reason. */
  unavailable?: ReadonlySet<string>;
  /** Make a new track list — offered at the foot of Lists. */
  onNewList?: () => void;

  // ── this screen's settings ──
  /** Only for screens that have some (a Recs screen's D W M · Long Short). */
  settings?: ReactNode;

  // ── tools ──
  /** Rows the grid is showing, or null before anything has loaded. */
  count: number | null;
  hasFilters: boolean;
  filtersOn: boolean;
  onFiltersToggle: () => void;
  /**
   * The ▲▼ key between the filter key and Columns: All → ▲ Gainers → ▼ Losers
   * (`lib/screener-direction.ts`). Omitted `onDirectionCycle` ⇒ no key.
   */
  direction?: "up" | "down";
  directionTitle?: string;
  onDirectionCycle?: () => void;
  columnsOpen: boolean;
  onColumnsToggle: () => void;
  onRefresh: () => void;
  refreshing: boolean;
  /**
   * Σ — chart the rows shown as one index (owner, 2026-10-19).
   * Omitted ⇒ no button; off below two rows.
   */
  onIndex?: () => void;
  /**
   * ▦ Leaders — the panel over this screen (`docs/plans/leader-screener.md`).
   * Omitted ⇒ no key: a screen that lays out its own grid, or no plan for it.
   */
  leaders?: { on: boolean; onToggle: () => void };
  /** Σ's weighting, picked in ⋯ › Index weighting (cap since 2026-10-21). */
  indexWeighting?: "equal" | "cap";
  onIndexWeighting?: (weighting: "equal" | "cap") => void;
  /**
   * A screen's own choices that live in ⋯ rather than on the bar — a TopDown's
   * layout (owner, 2026-10-16). One heading, ticked options.
   */
  moreSection?: { heading: string; options: { label: string; active: boolean; onSelect: () => void }[] };
  /** Hide the whole screener — ⋯ › Hide screener. Omitted ⇒ not offered. */
  onHide?: () => void;
  /**
   * ⋯ › Paste from clipboard / Import symbols… / Export symbols…. The first two
   * only while the screen is one of the user's lists; Export on any screen.
   * Each omitted ⇒ not offered. `docs/features/tracklists.md`.
   */
  listTools?: ListTools;
  /**
   * Bumped to open the ◎ picker from elsewhere — the command search's "Open the
   * group list". A nonce, so asking twice opens it twice.
   */
  openPickerNonce?: number;

  // ── shape ──
  /** Which side the screener is docked on, when the layout says. */
  dockSide?: ScreenerPlacement;
}

type Pop = "screens" | "picker" | "more" | "overflow" | null;

/** The screen's import and export, as ⋯ entries. Shared with the phone bar. */
export interface ListTools {
  onPaste?: () => void;
  onImport?: () => void;
  onExport?: () => void;
}

export function ScreenerBar(props: ScreenerBarProps) {
  const {
    screens,
    current,
    previous,
    recent,
    onPickScreen,
    onNewScreen,
    onEditScreen,
    types,
    groupsByType,
    selection,
    onSelectionChange,
    unavailable,
    onNewList,
    settings,
    count,
    hasFilters,
    filtersOn,
    onFiltersToggle,
    direction,
    directionTitle,
    onDirectionCycle,
    columnsOpen,
    onColumnsToggle,
    onRefresh,
    onIndex,
    leaders,
    indexWeighting = "equal",
    onIndexWeighting,
    moreSection,
    refreshing,
    onHide,
    listTools,
    openPickerNonce,
    dockSide,
  } = props;

  const rootRef = useRef<HTMLDivElement | null>(null);
  const screensDropRef = useRef<HTMLButtonElement | null>(null);
  const universeRef = useRef<HTMLButtonElement | null>(null);
  const moreRef = useRef<HTMLButtonElement | null>(null);
  const overflowRef = useRef<HTMLButtonElement | null>(null);
  const chipsRef = useRef<HTMLDivElement | null>(null);
  const rulerRef = useRef<HTMLDivElement | null>(null);

  const [pop, setPop] = useState<Pop>(null);
  const [width, setWidth] = useState(0);
  const [fit, setFit] = useState<OneRow>({ shown: [], hidden: [] });
  const [kind, setKind] = useState<string>(
    () => barPicked(selection)[0]?.groupType ?? types[0]?.groupType ?? ALL_KINDS,
  );
  const [step, setStep] = useState<1 | 2>(1);

  // Opened from outside (`openPickerNonce`), on the kinds step as a click opens it.
  // The first value is the mount's, not a request.
  const pickerAsked = useRef(openPickerNonce);
  useEffect(() => {
    if (openPickerNonce === pickerAsked.current) return;
    pickerAsked.current = openPickerNonce;
    setStep(1);
    setPop("picker");
  }, [openPickerNonce]);

  const tall = isTall(dockSide, width);
  const hasSettings = settings != null && settings !== false;
  /**
   * Wide with settings: the settings come first and the chips take what is
   * left (owner, 2026-10-16); only when not one chip fits does ◎ carry the
   * groups itself (`universeLabel`'s folded form).
   */
  const folded = hasSettings && !tall && fit.shown.length === 0 && barPicked(selection).length > 0;

  const close = useCallback(() => setPop(null), []);
  /**
   * A popover closed by a press outside it — which includes a press on its own
   * trigger. `Menu` closes on `pointerdown`, and the trigger's `click` follows
   * and would open it straight back; remembering which one the press closed
   * lets that click be the "close" it was meant as.
   */
  const dismissed = useRef<{ which: Pop; at: number }>({ which: null, at: 0 });
  const dismiss = useCallback(
    () =>
      setPop((open) => {
        dismissed.current = { which: open, at: Date.now() };
        return null;
      }),
    [],
  );
  const toggle = (which: Exclude<Pop, null>) => {
    const last = dismissed.current;
    dismissed.current = { which: null, at: 0 };
    if (last.which === which && Date.now() - last.at < 400) return;
    setPop((open) => (open === which ? null : which));
  };

  /* ── Selection edits — the data model is `GroupSelection`, unchanged ── */

  const patch = (next: Partial<GroupSelection>) => onSelectionChange({ ...selection, ...next });
  /** Single / Multiple (owner, 2026-10-27): the chips are Single's one group or the working set. */
  const single = selection.view === "single";
  const shown = barPicked(selection);
  const viewSel = single ? { ...selection, picked: shown } : selection;
  const isPicked = (groupType: string, codes: readonly string[]) =>
    selection.picked.some((p) => p.groupType === groupType && codes.includes(p.code));
  /** A merged entry is one tick and every code it stands for. */
  const togglePicked = (groupType: string, codes: readonly string[]) =>
    single
      ? onSelectionChange(
          isPicked(groupType, codes)
            ? codes.reduce((s, code) => removeSingle(s, groupType, code), selection)
            : pickSingle(selection, groupType, codes[0]!),
        )
      : patch({
      picked: isPicked(groupType, codes)
        ? selection.picked.filter((p) => !(p.groupType === groupType && codes.includes(p.code)))
        : [
            ...selection.picked,
            ...codes
              .filter((code) => !isPicked(groupType, [code]))
              .map((code) => ({ groupType, code, on: true })),
          ],
    });
  const toggleOn = (p: PickedGroup) =>
    single
      ? onSelectionChange(pickSingle(selection, p.groupType, p.code))
      : patch({
      picked: selection.picked.map((x) =>
        x.groupType === p.groupType && x.code === p.code ? { ...x, on: !x.on } : x,
      ),
    });
  const removePicked = (p: PickedGroup) =>
    single
      ? onSelectionChange(removeSingle(selection, p.groupType, p.code))
      : patch({
      picked: selection.picked.filter((x) => !(x.groupType === p.groupType && x.code === p.code)),
    });

  const onGroups = shown.filter((p) => p.on).length;
  const symbols = onCount(viewSel, groupsByType);

  /* ── Markets — the narrowing the old bar had, now under ⋯ ── */

  const marketOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const list of groupsByType.values()) {
      for (const g of list) if (g.market && !g.locked) seen.add(g.market);
    }
    return [...seen].sort();
  }, [groupsByType]);
  const inMarkets = useCallback(
    (g: { market?: string | null }) =>
      selection.markets.length === 0 || g.market == null || selection.markets.includes(g.market),
    [selection.markets],
  );
  const groupsOf = useCallback(
    (groupType: string): MergedGroup[] =>
      groupType === ALL_KINDS
        ? types.flatMap((t) => mergeAcrossMarkets((groupsByType.get(t.groupType) ?? []).filter(inMarkets)))
        : mergeAcrossMarkets((groupsByType.get(groupType) ?? []).filter(inMarkets)),
    [types, groupsByType, inMarkets],
  );

  /* ── Measurement ── */

  const pickedGroups = shown
    .map((p) => ({ p, g: (groupsByType.get(p.groupType) ?? []).find((x) => x.code === p.code) }))
    .filter((x): x is { p: PickedGroup; g: SymbolGroup } => x.g !== undefined);

  const measure = useCallback(() => {
    const root = rootRef.current;
    if (root) setWidth((w) => (w === root.clientWidth ? w : root.clientWidth));
    const box = chipsRef.current;
    const ruler = rulerRef.current;
    if (!box || !ruler) return;
    const widths = Array.from(ruler.children).map((c) => (c as HTMLElement).offsetWidth);
    const over = overflowRef.current;
    const budget = box.clientWidth + (over ? over.offsetWidth + CHIP_GAP : 0);
    // "Not laid out yet" is the *bar* at 0, never the chip area: a laid-out
    // chip area is 0 when its own `+N` takes the room, and reading that as
    // "show every chip" removed the `+N`, which gave the room back — React
    // #185 at 214px, docked right (2026-10-31).
    const next =
      (root?.clientWidth ?? 0) > 0
        ? fillRow(widths, budget)
        : { shown: widths.map((_, i) => i), hidden: [] };
    setFit((prev) => (same(prev.shown, next.shown) && same(prev.hidden, next.hidden) ? prev : next));
  }, []);

  useLayoutEffect(() => {
    measure();
  });

  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(root);
    return () => observer.disconnect();
  }, [measure]);

  /* ── Pieces ── */

  // Three when a wide bar has the room, two otherwise; names get more room wide.
  const slots = !tall && width >= THREE_SCREENS_FROM ? 3 : 2;
  const [currentShown, ...others] = recentScreens(screens, current, recent ?? (previous ? [previous] : []), slots);
  const screenBtn = { ...SCREEN_BTN, maxWidth: tall ? SCREEN_BTN.maxWidth : "24ch" };

  const screensPart = (
    <div data-tour="screens" style={SCREENS}>
      {currentShown && (
        <button
          type="button"
          style={{ ...BTN, ...ON, ...screenBtn }}
          title={`${currentShown.name} — the screen showing. Click for every screen`}
          aria-current="true"
          onClick={() => toggle("screens")}
        >
          <ScreenIcon />
          <span style={ELLIPSIS}>{currentShown.name}</span>
        </button>
      )}
      {others.map((s) => (
        <button
          key={s.key}
          type="button"
          style={{ ...BTN, ...screenBtn, color: "var(--qf-text-secondary)" }}
          title={`Switch to ${s.name}`}
          onClick={() => {
            close();
            onPickScreen(s.key);
          }}
        >
          <span style={ELLIPSIS}>{s.name}</span>
        </button>
      ))}
      <button
        ref={screensDropRef}
        type="button"
        style={{ ...BTN, ...ICON_BTN }}
        aria-label="All screens"
        title="All screens"
        onClick={() => toggle("screens")}
      >
        ▾
      </button>
    </div>
  );

  const label = universeLabel(viewSel, groupsByType, types, folded);
  const universeButton = (
    <button
      ref={universeRef}
      type="button"
      data-tour="universe"
      style={{ ...BTN, fontWeight: 600, flex: "0 1 auto", minWidth: 60, maxWidth: folded ? 260 : 180 }}
      title={`Universe: ${label}`}
      aria-haspopup="dialog"
      onClick={() => {
        setStep(1);
        toggle("picker");
      }}
    >
      <span aria-hidden>◎</span>
      <span style={ELLIPSIS}>{label}</span>
      <span aria-hidden style={{ color: "var(--qf-text-muted)", fontSize: 10 }}>▾</span>
    </button>
  );

  const combine = !single && onGroups >= 2 && (
    <Segmented value={selection.combine} onChange={(c) => patch({ combine: c })} />
  );

  const chip = (p: PickedGroup, g: SymbolGroup, key?: string) => (
    <button
      key={key ?? `${p.groupType}:${p.code}`}
      type="button"
      style={chipStyle(p.on)}
      aria-pressed={p.on}
      title={
        single
          ? p.on
            ? `${g.name} — on screen`
            : `${g.name} — click to screen this instead`
          : p.on
            ? `${g.name} — click to switch off`
            : `${g.name} — switched off, click to switch on`
      }
      onClick={() => toggleOn(p)}
    >
      {chipInner(
        g,
        <span
          role="button"
          tabIndex={0}
          aria-label={`Remove ${g.name}`}
          title="Remove from the universe"
          style={{ display: "inline-flex" }}
          onClick={(event) => {
            event.stopPropagation();
            removePicked(p);
          }}
          onKeyDown={(event) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            event.stopPropagation();
            removePicked(p);
          }}
        >
          <CloseIcon size={9} />
        </span>,
      )}
    </button>
  );

  const chipsPart = (
    <>
      <div ref={chipsRef} style={CHIPS} data-tour="group-chips">
        {pickedGroups.length === 0 ? (
          <span style={HINT}>Pick a group from ◎</span>
        ) : (
          fit.shown.map((i) => pickedGroups[i]).filter(Boolean).map((x) => chip(x!.p, x!.g))
        )}
      </div>
      {fit.hidden.length > 0 && (
        <button
          ref={overflowRef}
          type="button"
          style={{ ...BTN, flex: "0 0 auto" }}
          title={`${fit.hidden.length} more`}
          onClick={() => toggle("overflow")}
        >
          +{fit.hidden.length} ▾
        </button>
      )}
      {combine}
    </>
  );

  const showColumnsWord = !tall && width >= COLUMNS_WORD_FROM;
  const tools = (
    <div style={TOOLS}>
      {leaders && (
        <button
          type="button"
          style={leaders.on ? { ...BTN, ...ON } : BTN}
          aria-pressed={leaders.on}
          aria-label="Boxes"
          title={leaders.on ? "Boxes on — click to show the whole screen" : "Boxes: the growth boxes, filtered by ERG Rank, for any date"}
          onClick={leaders.onToggle}
        >
          <span aria-hidden>▦</span>
          {!tall && width >= COLUMNS_WORD_FROM && <span>Boxes</span>}
        </button>
      )}
      {onIndex && (
        <button
          type="button"
          style={{ ...BTN, ...ICON_BTN }}
          aria-label="Chart as index"
          title={
            count === null
              ? "Rows still loading"
              : count < 2
                ? "Needs at least 2 rows to make an index"
                : `Chart these ${count.toLocaleString()} as one ${indexWeighting === "cap" ? "cap" : "equal"}-weight index`
          }
          onClick={onIndex}
          disabled={count === null || count < 2}
        >
          <SigmaIcon />
        </button>
      )}
      <button
        type="button"
        style={filtersOn && hasFilters ? { ...BTN, ...ON } : BTN}
        title={
          !hasFilters
            ? "No filter selected"
            : filtersOn
              ? "Filters on — click to turn off"
              : "Filters off — click to turn on"
        }
        onClick={onFiltersToggle}
        disabled={!hasFilters}
      >
        {filtersOn && hasFilters ? <FilterIcon /> : <FilterOffIcon />}
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {count === null ? "—" : count.toLocaleString()}
        </span>
      </button>
      {onDirectionCycle && (
        <button
          type="button"
          style={{
            ...BTN,
            ...ICON_BTN,
            ...(direction === "up"
              ? { color: "var(--qf-positive)", borderColor: "var(--qf-positive)" }
              : direction === "down"
                ? { color: "var(--qf-negative)", borderColor: "var(--qf-negative)" }
                : null),
          }}
          aria-label={direction === "up" ? "Gainers" : direction === "down" ? "Losers" : "All rows"}
          title={directionTitle}
          onClick={onDirectionCycle}
        >
          {direction === "up" ? "▲" : direction === "down" ? "▼" : <span style={{ fontSize: 8, letterSpacing: -1 }}>▲▼</span>}
        </button>
      )}
      <button
        type="button"
        style={columnsOpen ? { ...BTN, background: "var(--qf-selection)" } : showColumnsWord ? BTN : { ...BTN, ...ICON_BTN }}
        title="Choose columns"
        aria-label="Columns"
        onClick={onColumnsToggle}
      >
        <ColumnsIcon />
        {showColumnsWord && <span>Columns</span>}
      </button>
      {!tall && (
        <button
          type="button"
          style={{ ...BTN, ...ICON_BTN }}
          aria-label="Refresh"
          title="Refresh the screen"
          onClick={onRefresh}
          disabled={refreshing}
        >
          <RefreshIcon spinning={refreshing} />
        </button>
      )}
      <button
        ref={moreRef}
        type="button"
        style={{ ...BTN, ...ICON_BTN }}
        aria-label="More screener tools"
        title="More"
        onClick={() => toggle("more")}
      >
        ⋯
      </button>
    </div>
  );

  const settingsPart = hasSettings && (
    <div role="group" aria-label="Screen settings" style={{ display: "flex", alignItems: "center", flex: "0 0 auto" }}>
      {settings}
    </div>
  );

  /* ── Popovers ── */

  const sections = screenSections(screens);
  const screensMenu = pop === "screens" && (
    <Menu anchorRef={screensDropRef} root={rootRef} onClose={dismiss}>
      <div role="listbox" aria-label="Screens" style={{ padding: "4px 0", minWidth: 220 }}>
        {sections.map((section) => (
          <div key={section.title} role="group" aria-label={section.title}>
            <div style={HEADING}>{section.title}</div>
            {section.screens.map((s) => {
              const active = s.key === currentShown?.key;
              return (
                <button
                  key={s.key}
                  type="button"
                  role="option"
                  aria-selected={active}
                  style={active ? { ...ROW, background: "var(--qf-selection)", fontWeight: 600 } : ROW}
                  title={isClub(s) ? `${s.name} — ${s.ownerKey}'s screen` : `Show ${s.name}`}
                  onClick={() => {
                    close();
                    if (!active) onPickScreen(s.key);
                  }}
                >
                  <span style={{ width: 12, color: "var(--qf-accent)" }}>{active ? "✓" : ""}</span>
                  <span style={ELLIPSIS}>{s.name}</span>
                  {isClub(s) && s.ownerKey && <span style={BADGE}>{s.ownerKey}</span>}
                </button>
              );
            })}
          </div>
        ))}
        {(onNewScreen || onEditScreen) && (
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "6px 10px 2px", borderTop: "1px solid var(--qf-border)", marginTop: 4 }}>
            {onNewScreen && (
              <button type="button" style={LINK} onClick={() => { close(); onNewScreen(); }}>
                ＋ New screen
              </button>
            )}
            {onEditScreen && (
              <button type="button" style={LINK} onClick={() => { close(); onEditScreen(); }}>
                ✎ Edit this screen
              </button>
            )}
          </div>
        )}
      </div>
    </Menu>
  );

  const kinds = kindRows(types, viewSel);
  const kindList = (chevron: boolean) => (
    <div role="list" aria-label="Kinds" style={{ padding: "4px 0" }}>
      {kinds.map((k) => (
        <button
          key={k.groupType}
          type="button"
          role="listitem"
          style={!chevron && k.groupType === kind ? { ...ROW, background: "var(--qf-selection)", fontWeight: 600 } : ROW}
          onClick={() => {
            setKind(k.groupType);
            if (chevron) setStep(2);
          }}
        >
          <span style={{ flex: 1 }}>{k.label}</span>
          {k.picked > 0 ? (
            <span style={{ color: "var(--qf-accent)", fontWeight: 600 }}>· {k.picked}</span>
          ) : k.count !== null ? (
            <span style={{ color: "var(--qf-text-muted)" }}>{k.count}</span>
          ) : null}
          {chevron && <span aria-hidden style={{ color: "var(--qf-text-muted)" }}>›</span>}
        </button>
      ))}
    </div>
  );

  const groupList = (
    <div role="group" aria-label={kinds.find((k) => k.groupType === kind)?.label ?? "Groups"} style={{ padding: "4px 0", maxHeight: 280, overflowY: "auto" }}>
      {groupsOf(kind).map((g) => {
        const locked = g.locked === true;
        const disabled = locked || (unavailable?.has(g.code) ?? false);
        const ticked = isPicked(g.groupType, g.codes);
        return (
          <label
            key={`${g.groupType}:${g.codes.join(",")}`}
            style={{ ...ROW, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.55 : 1 }}
            title={
              locked
                ? `${g.name} — not included in your plan`
                : disabled
                  ? `${g.name} — no screen data yet`
                  : `${g.name}${g.description ? ` — ${g.description}` : ""}`
            }
          >
            <input
              type="checkbox"
              checked={ticked}
              disabled={disabled}
              onChange={() => togglePicked(g.groupType, g.codes)}
            />
            <span style={{ ...ELLIPSIS, flex: 1 }}>{g.name}</span>
            <span style={{ color: "var(--qf-text-muted)", fontVariantNumeric: "tabular-nums" }}>
              {g.resolvedAt === null ? "—" : g.memberCount.toLocaleString()}
            </span>
          </label>
        );
      })}
      {onNewList && kind === TRACK_LIST_TYPE && (
        <button type="button" style={{ ...ROW, color: "var(--qf-accent)" }} onClick={() => { close(); onNewList(); }}>
          + New list…
        </button>
      )}
    </div>
  );

  const pickerFoot = (
    <div style={FOOT}>
      <span>
        {onGroups} on{symbols === null ? "" : ` · ${symbols.toLocaleString("en-US")} symbols`}
      </span>
    </div>
  );
  /** The mode row at the top of ◎ — Single / Multiple, and OR/AND once 2+ are on. */
  const pickerMode = (
    <div style={{ ...FOOT, borderTop: 0, borderBottom: "1px solid var(--qf-border)" }}>
      <ModeSwitch value={selection.view} onChange={(v) => onSelectionChange(withView(selection, v))} />
      {combine}
    </div>
  );

  const picker = pop === "picker" && (
    <Menu anchorRef={universeRef} root={rootRef} onClose={dismiss} tourId="universe-picker">
      {tall ? (
        <div style={{ width: 300, maxWidth: "calc(100vw - 24px)" }}>
          {pickerMode}
          {step === 1 ? (
            kindList(true)
          ) : (
            <>
              <button
                type="button"
                style={{ ...ROW, fontWeight: 600, borderBottom: "1px solid var(--qf-border)" }}
                aria-label="Back to kinds"
                onClick={() => setStep(1)}
              >
                ‹ {kinds.find((k) => k.groupType === kind)?.label ?? "Groups"}
              </button>
              {groupList}
            </>
          )}
          {pickerFoot}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "170px 1fr", width: 470, maxWidth: "calc(100vw - 24px)" }}>
          <div style={{ gridColumn: "1 / -1" }}>{pickerMode}</div>
          <div style={{ borderRight: "1px solid var(--qf-border)" }}>{kindList(false)}</div>
          {groupList}
          <div style={{ gridColumn: "1 / -1" }}>{pickerFoot}</div>
        </div>
      )}
    </Menu>
  );

  const overflowMenu = pop === "overflow" && (
    <Menu anchorRef={overflowRef} root={rootRef} onClose={dismiss} align="right" title={`${fit.hidden.length} more`}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: CHIP_GAP, padding: 8, maxWidth: 420 }}>
        {fit.hidden
          .map((i) => pickedGroups[i])
          .filter(Boolean)
          .map((x) => chip(x!.p, x!.g, `overflow-${x!.p.groupType}-${x!.p.code}`))}
      </div>
    </Menu>
  );

  const moreMenu = pop === "more" && (
    <Menu anchorRef={moreRef} root={rootRef} onClose={dismiss} align="right">
      {tall && (
        <MenuRow active={false} onSelect={() => { close(); onRefresh(); }}>
          <span>Refresh</span>
          <RefreshIcon spinning={refreshing} />
        </MenuRow>
      )}
      {marketOptions.length > 1 && (
        <>
          <div style={HEADING}>Markets</div>
          {marketOptions.map((code) => {
            // Empty means every market, so every box reads as ticked.
            const on = selection.markets.length === 0 || selection.markets.includes(code);
            return (
              <MenuRow
                indent
                key={code}
                active={on}
                onSelect={() => {
                  const now = selection.markets.length === 0 ? marketOptions : selection.markets;
                  const next = on ? now.filter((m) => m !== code) : [...now, code];
                  patch({ markets: next.length === 0 ? [] : next });
                }}
              >
                <span>{marketByCode(code).label}</span>
                <span style={{ color: "var(--qf-accent)" }}>{on ? "✓" : ""}</span>
              </MenuRow>
            );
          })}
        </>
      )}
      {moreSection && (
        <>
          <div style={HEADING}>{moreSection.heading}</div>
          {moreSection.options.map((o) => (
            <MenuRow indent key={o.label} active={o.active} onSelect={() => { close(); o.onSelect(); }}>
              <span>{o.label}</span>
              <span style={{ color: "var(--qf-accent)" }}>{o.active ? "✓" : ""}</span>
            </MenuRow>
          ))}
        </>
      )}
      {onIndex && (
        <>
          <div style={HEADING}>Index weighting</div>
          {(["equal", "cap"] as const).map((w) => (
            <MenuRow indent key={w} active={indexWeighting === w} onSelect={() => { close(); onIndexWeighting?.(w); }}>
              <span>{w === "equal" ? "Equal" : "Cap"}</span>
              <span style={{ color: "var(--qf-accent)" }}>{indexWeighting === w ? "✓" : ""}</span>
            </MenuRow>
          ))}
        </>
      )}
      {listTools?.onPaste && (
        <MenuRow active={false} onSelect={() => { close(); listTools.onPaste?.(); }}>
          <span>Paste from clipboard</span>
          <span />
        </MenuRow>
      )}
      {listTools?.onImport && (
        <MenuRow active={false} onSelect={() => { close(); listTools.onImport?.(); }}>
          <span>Import symbols…</span>
          <span />
        </MenuRow>
      )}
      {listTools?.onExport && (
        <MenuRow active={false} onSelect={() => { close(); listTools.onExport?.(); }}>
          <span>Export symbols…</span>
          <span />
        </MenuRow>
      )}
      {onHide && (
        <MenuRow active={false} onSelect={() => { close(); onHide(); }}>
          <span>Hide screener</span>
          <CloseIcon size={10} />
        </MenuRow>
      )}
      {!tall && marketOptions.length <= 1 && !onHide && !moreSection && !listTools?.onExport && !onIndex && (
        <div style={{ ...HINT, padding: "6px 10px" }}>Nothing more here</div>
      )}
    </Menu>
  );

  /* ── Rows ── */

  return (
    <div
      ref={rootRef}
      style={BAR}
      data-shape={tall ? "tall" : "wide"}
      role="toolbar"
      aria-label="Screener"
    >
      <style>{SPIN_KEYFRAMES}</style>
      {/* The ruler: every chip, off-screen, never interactive. */}
      <div ref={rulerRef} aria-hidden style={RULER}>
        {pickedGroups.map(({ p, g }) => (
          <span key={`ruler-${p.groupType}-${p.code}`} style={chipStyle(true)}>
            {chipInner(g, null)}
          </span>
        ))}
      </div>

      {tall ? (
        <>
          <div style={ROW_STYLE}>
            {screensPart}
            <div style={{ flex: 1, minWidth: 4 }} />
            {tools}
          </div>
          <div style={{ ...ROW_STYLE, paddingTop: 0 }}>
            {universeButton}
            {chipsPart}
          </div>
          {hasSettings && <div style={{ ...ROW_STYLE, paddingTop: 0 }}>{settingsPart}</div>}
        </>
      ) : (
        <div style={ROW_STYLE}>
          {screensPart}
          <span style={SEP} />
          {universeButton}
          <span style={SEP} />
          {settingsPart}
          {hasSettings && <span style={SEP} />}
          {chipsPart}
          {tools}
        </div>
      )}

      {screensMenu}
      {picker}
      {overflowMenu}
      {moreMenu}
    </div>
  );
}

/* ── Chips ─────────────────────────────────────────────────────────────── */

/** Same markup for the chip and its ruler twin — see the header. */
function chipInner(g: SymbolGroup, slot: ReactNode) {
  return (
    <>
      <span>{chipLabel(g)}</span>
      <span style={{ fontStyle: "italic", opacity: 0.75, marginLeft: 4 }}>
        {g.resolvedAt === null ? "—" : g.memberCount.toLocaleString()}
      </span>
      <span style={{ display: "inline-flex", width: 13, marginLeft: 5, marginRight: -2, justifyContent: "center" }}>
        {slot}
      </span>
    </>
  );
}

function chipStyle(on: boolean): React.CSSProperties {
  return on
    ? { ...CHIP, ...ON }
    : {
        ...CHIP,
        color: "var(--qf-text-muted)",
        background: "var(--qf-surface-alt)",
        textDecoration: "line-through",
      };
}

function same(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/** OR / AND, as one control. */
function ModeSwitch({ value, onChange }: { value: GroupSelection["view"]; onChange: (v: GroupSelection["view"]) => void }) {
  const cell = (v: GroupSelection["view"], first: boolean): React.CSSProperties => ({
    font: "inherit",
    fontSize: 11,
    lineHeight: "18px",
    padding: "2px 10px",
    border: 0,
    borderLeft: first ? 0 : "1px solid var(--qf-border)",
    background: value === v ? "var(--qf-accent)" : "var(--qf-background)",
    color: value === v ? "var(--qf-background)" : "var(--qf-text-secondary)",
    fontWeight: 600,
    cursor: "pointer",
  });
  return (
    <div
      role="group"
      aria-label="Select mode"
      style={{ display: "inline-flex", flex: "0 0 auto", border: "1px solid var(--qf-border)", borderRadius: 4, overflow: "hidden" }}
    >
      <button type="button" style={cell("single", true)} aria-pressed={value === "single"} onClick={() => onChange("single")}>
        Single
      </button>
      <button type="button" style={cell("multi", false)} aria-pressed={value === "multi"} onClick={() => onChange("multi")}>
        Multiple
      </button>
    </div>
  );
}

function Segmented({ value, onChange }: { value: CombineMode; onChange: (v: CombineMode) => void }) {
  const cell = (v: CombineMode, first: boolean): React.CSSProperties => ({
    font: "inherit",
    fontSize: 11,
    lineHeight: "18px",
    padding: "2px 8px",
    border: 0,
    borderLeft: first ? 0 : "1px solid var(--qf-border)",
    background: value === v ? "var(--qf-accent)" : "var(--qf-background)",
    color: value === v ? "var(--qf-background)" : "var(--qf-text-secondary)",
    fontWeight: 600,
    cursor: "pointer",
  });
  return (
    <div
      role="group"
      aria-label="Combine groups"
      style={{ display: "inline-flex", flex: "0 0 auto", border: "1px solid var(--qf-border)", borderRadius: 4, overflow: "hidden" }}
    >
      <button type="button" style={cell("or", true)} aria-pressed={value === "or"} onClick={() => onChange("or")}>
        OR
      </button>
      <button type="button" style={cell("and", false)} aria-pressed={value === "and"} onClick={() => onChange("and")}>
        AND
      </button>
    </div>
  );
}

/* ── Icons ─────────────────────────────────────────────────────────────── */

function ScreenIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <rect x="1.6" y="2.6" width="12.8" height="10.8" rx="1.3" />
      <line x1="1.6" y1="6.2" x2="14.4" y2="6.2" />
      <line x1="1.6" y1="9.8" x2="14.4" y2="9.8" />
    </svg>
  );
}

function RefreshIcon({ spinning }: { spinning?: boolean }) {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      aria-hidden="true"
      style={spinning ? { animation: "qf-spin 0.75s linear infinite" } : undefined}
    >
      <path d="M13.4 8a5.4 5.4 0 1 1-1.6-3.8" />
      <path d="M13.6 2.2v3.1h-3.1" />
    </svg>
  );
}

function ColumnsIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" aria-hidden="true">
      <rect x="1.6" y="2.6" width="12.8" height="10.8" rx="1.3" />
      <line x1="6" y1="2.6" x2="6" y2="13.4" />
      <line x1="10" y1="2.6" x2="10" y2="13.4" />
    </svg>
  );
}

/** Σ: the rows shown, summed into one index. */
function SigmaIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" aria-hidden="true">
      <path d="M12.6 3.4V2.4H3.4L8.4 8L3.4 13.6H12.6V12.6" />
    </svg>
  );
}

/** A filled funnel: filters applied. */
function FilterIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M2 3h12l-4.6 5.4v4L6.6 14V8.4L2 3Z" fill="currentColor" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}

/** The same funnel, hollow and struck through: filters not applied. */
function FilterOffIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" strokeLinecap="round" aria-hidden="true">
      <path d="M2 3h12l-4.6 5.4v4L6.6 14V8.4L2 3Z" opacity="0.75" />
      <line x1="2.4" y1="13.6" x2="13.6" y2="2.4" />
    </svg>
  );
}

const SPIN_KEYFRAMES = `@keyframes qf-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { @keyframes qf-spin { to { transform: none; } } }`;

/* ── Styles ────────────────────────────────────────────────────────────── */

const BAR: React.CSSProperties = {
  position: "relative",
  borderBottom: "1px solid var(--qf-border)",
  background: "var(--qf-surface)",
  overflow: "hidden",
  // Never shrink: the bar's height is content, not a share of the leftovers
  // (a 390px screen once crushed the old bar to 3px).
  flex: "0 0 auto",
};

const ROW_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: CHIP_GAP,
  padding: "5px 8px",
  minHeight: 34,
  minWidth: 0,
};

const BTN: React.CSSProperties = {
  font: "inherit",
  fontSize: 12,
  lineHeight: "18px",
  padding: "2px 8px",
  borderRadius: 4,
  border: "1px solid var(--qf-border)",
  background: "var(--qf-background)",
  color: "var(--qf-text)",
  cursor: "pointer",
  whiteSpace: "nowrap",
  flex: "0 0 auto",
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
};

const ICON_BTN: React.CSSProperties = {
  minWidth: 26,
  padding: "2px 6px",
  justifyContent: "center",
  color: "var(--qf-text-secondary)",
};

/** The accent fill that means "on" — see the header. */
const ON: React.CSSProperties = {
  background: "var(--qf-accent)",
  borderColor: "var(--qf-accent)",
  color: "var(--qf-background)",
  fontWeight: 600,
};

const SCREEN_BTN: React.CSSProperties = {
  flex: "0 1 auto",
  minWidth: 40,
  maxWidth: "16ch",
  fontWeight: 600,
};

const SCREENS: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  minWidth: 0,
  flex: "0 1 auto",
};

const ELLIPSIS: React.CSSProperties = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const SEP: React.CSSProperties = {
  width: 1,
  alignSelf: "stretch",
  background: "var(--qf-border)",
  flex: "0 0 auto",
};

const CHIPS: React.CSSProperties = {
  display: "flex",
  gap: CHIP_GAP,
  flex: "1 1 auto",
  overflow: "hidden",
  minWidth: 0,
  alignItems: "center",
};

const CHIP: React.CSSProperties = {
  ...BTN,
  borderRadius: 999,
  padding: "2px 8px 2px 10px",
  gap: 0,
};

const TOOLS: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  flex: "0 0 auto",
};

const HINT: React.CSSProperties = {
  fontSize: 11,
  color: "var(--qf-text-muted)",
  whiteSpace: "nowrap",
};

const RULER: React.CSSProperties = {
  position: "absolute",
  left: -99999,
  top: 0,
  display: "flex",
  gap: CHIP_GAP,
  pointerEvents: "none",
  visibility: "hidden",
};

const ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  font: "inherit",
  fontSize: 12,
  // Explicit, so the selected row's 600 is a change of value rather than a
  // property appearing beside the `font` shorthand (React warns on that).
  fontWeight: 400,
  textAlign: "left",
  padding: "5px 10px",
  border: 0,
  background: "transparent",
  color: "var(--qf-text)",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const HEADING: React.CSSProperties = {
  padding: "6px 10px 2px",
  fontSize: 10,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--qf-text-muted)",
};

const BADGE: React.CSSProperties = {
  marginLeft: "auto",
  fontSize: 9,
  padding: "0 4px",
  borderRadius: 3,
  border: "1px solid var(--qf-border)",
  color: "var(--qf-text-muted)",
};

const LINK: React.CSSProperties = {
  font: "inherit",
  fontSize: 12,
  border: 0,
  background: "transparent",
  color: "var(--qf-accent)",
  cursor: "pointer",
  padding: "2px 0",
};

const FOOT: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  padding: "8px 10px",
  borderTop: "1px solid var(--qf-border)",
  fontSize: 12,
  color: "var(--qf-text-secondary)",
};
